import type { Recording } from '../types/portal';
import { supabase } from './supabase';

// A lesson counts as "new" if it was added recently and the student
// hasn't completed it yet.
export const NEW_WINDOW_DAYS = 14;

export function isNewRecording(rec: Recording, completed: boolean): boolean {
  if (completed) return false;
  const created = new Date(rec.created_at).getTime();
  return Date.now() - created < NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

// Signed-URL lifetime for private storage assets (materials, certificates).
// Short enough to limit link-sharing damage, long enough for the download to
// complete comfortably even on slow connections.
export const SIGNED_URL_TTL_SECONDS = 60 * 10;

export async function createSignedFileUrl(
  bucket: 'material-files' | 'certificates',
  path: string,
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    if (import.meta.env.DEV) console.error('createSignedFileUrl failed:', error);
    return null;
  }
  return data.signedUrl;
}

// Derive the object path inside a Supabase Storage bucket from a stored URL.
// Handles the public, signed, authenticated and render URL shapes. Returns
// null when the URL does not point at that bucket (e.g. a genuinely external
// link) so the caller knows to use the URL as-is.
export function storagePathFromUrl(bucket: string, url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(
    new RegExp(`/storage/v1/(?:object|render/image)/(?:public|sign|authenticated)/${bucket}/([^?]+)`),
  );
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

// Resolve the URL to actually open for a stored file:
//   1. an explicit storage_path  → short-lived signed URL (private bucket)
//   2. a Supabase storage URL     → derive the path, then a signed URL
//   3. a genuinely external link  → returned unchanged
// Returns null only when a file that lives in our (now private) bucket cannot
// be signed — the caller should surface an error instead of opening a broken
// tab on the old public URL, which would 400 for a private bucket.
export async function resolveFileUrl(
  bucket: 'material-files' | 'certificates',
  storagePath: string | null | undefined,
  fallbackUrl: string | null | undefined,
): Promise<string | null> {
  const path = storagePath ?? storagePathFromUrl(bucket, fallbackUrl);
  if (path) return createSignedFileUrl(bucket, path);
  // Not in our storage — an external link that should be used directly.
  return fallbackUrl ?? null;
}

